import { supabase } from '@/lib/supabase';
import { adminError } from '@/lib/utils/admin-logger';

export interface WalletSummary {
  planCode: string;
  planName: string;
  monthlyAllowance: number;
  planCredits: number;
  rolloverCredits: number;
  /** When carried-over credits lapse (paid plans only). */
  rolloverExpiresAt: string | null;
  topupCredits: number;
  heldCredits: number;
  availableCredits: number;
  periodStart: string | null;
  periodEnd: string | null;
  usedCredits: number;
  subscriptionStatus: string | null;
}

export interface WalletDisplayTotals {
  remaining: number;
  used: number;
  reserved: number;
  total: number;
  remainingPercent: number;
  usedPercent: number;
  reservedPercent: number;
  isLow: boolean;
}

const safeCreditValue = (value: number | null | undefined): number =>
  Number.isFinite(value) ? Math.max(0, Number(value)) : 0;

/** Stable, non-negative totals for the Activity credit meter. */
export const walletDisplayTotals = (wallet: WalletSummary): WalletDisplayTotals => {
  const remaining = safeCreditValue(wallet.availableCredits);
  const used = safeCreditValue(wallet.usedCredits);
  const reserved = safeCreditValue(wallet.heldCredits);
  const total = used + remaining + reserved;
  const percent = (value: number) => (total > 0 ? (value / total) * 100 : 0);

  return {
    remaining,
    used,
    reserved,
    total,
    remainingPercent: percent(remaining),
    usedPercent: percent(used),
    reservedPercent: percent(reserved),
    isLow: total > 0 && remaining / total <= 0.1,
  };
};

export interface CreditPlan {
  planCode: string;
  displayName: string;
  monthlyCredits: number;
  priceEurMonthly: number;
  sortOrder: number;
}

/** Fractional credit balance for the signed-in user. */
export const fetchWalletSummary = async (): Promise<WalletSummary | null> => {
  try {
    const { data, error } = await supabase.rpc('wallet_summary_v2');
    if (error) {
      adminError('Error fetching wallet summary:', error);
      return null;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return {
      planCode: row.plan_code,
      planName: row.plan_name,
      monthlyAllowance: Number(row.monthly_allowance ?? 0),
      planCredits: Number(row.plan_credits ?? 0),
      rolloverCredits: Number(row.rollover_credits ?? 0),
      rolloverExpiresAt: row.rollover_expires_at ?? null,
      topupCredits: Number(row.topup_credits ?? 0),
      heldCredits: Number(row.held_credits ?? 0),
      availableCredits: Number(row.available_credits ?? 0),
      periodStart: row.period_start ?? null,
      periodEnd: row.period_end ?? null,
      usedCredits: Number(row.used_credits ?? 0),
      subscriptionStatus: row.subscription_status ?? null,
    };
  } catch (error) {
    adminError('Error in fetchWalletSummary:', error);
    return null;
  }
};

/** Active subscription plans, cheapest first. */
export const fetchCreditPlans = async (): Promise<CreditPlan[]> => {
  try {
    const { data, error } = await supabase
      .from('credit_plans')
      .select('plan_code, display_name, monthly_credits, price_eur_monthly, sort_order')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      adminError('Error fetching credit plans:', error);
      return [];
    }

    return (data ?? []).map((p) => ({
      planCode: p.plan_code,
      displayName: p.display_name,
      monthlyCredits: Number(p.monthly_credits ?? 0),
      priceEurMonthly: Number(p.price_eur_monthly ?? 0),
      sortOrder: p.sort_order ?? 0,
    }));
  } catch (error) {
    adminError('Error in fetchCreditPlans:', error);
    return [];
  }
};

/** Round for display: one decimal, no trailing ".0". */
export const formatCredits = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};
